/**
 * Onda Água v2 — the CAMERA-FOLLOWING RADIAL SEA MESH (geometry clipmap).
 *
 * WHY. The sea used to be one uniform 192² grid over 1500 m: a 7.81 m quad right under the bowsprit AND
 * at the horizon. Under the v1 per-pixel lighting the shading is fine, but the wave SILHOUETTE next to
 * the hull is a 7.8 m polygon — the last obviously-low-poly thing left in the near field.
 *
 * WHAT. Concentric square rings ("levels") around the captain: level 0 is a dense grid of `CELL0` quads,
 * every next level DOUBLES the cell size and DOUBLES the reach, so the vertex budget buys resolution
 * exactly where it is seen. One mesh, one draw call, 44% FEWER triangles than the old grid.
 *
 *   level  cell     covers (half-extent)      cells
 *   0       2 m    ±72 m       (full grid)   72×72
 *   1       4 m    ±144 m      (ring)        72²−36²
 *   2       8 m    ±288 m      (ring)        72²−36²
 *   3      16 m    ±576 m      (ring)        72²−36²
 *   4      32 m    ±1152 m     (ring)        72²−36²
 *
 *   41 472 triangles / 21 745 vertices, vs 73 728 / 37 249 for the uniform grid it replaces.
 *
 * THE TWO HARD PROBLEMS, AND HOW THIS SOLVES THEM
 *
 * 1. VERTEX SWIMMING. The wave height is a function of WORLD position (the shader evaluates the shared
 *    `oceanSample` at `modelMatrix * position`), so the surface is only stable if every vertex keeps
 *    landing on the SAME world lattice as the mesh follows the ship. `OCEAN_SNAP` is the COARSEST cell
 *    (32 m) and every finer cell divides it, so a snapped translation maps the whole clipmap — all five
 *    levels — onto one fixed global 2 m lattice. Nothing ever slides across the wave field: zero
 *    swimming, zero shimmer, at ANY level. (The old mesh snapped to 1 m over a 7.81 m grid, i.e. it DID
 *    slide — the shimmer was just small.)
 *
 * 2. CRACKS AT THE LOD SEAM. Where a fine level meets the next coarse one there is a T-junction: the fine
 *    edge has a vertex every `c`, the coarse edge every `2c`, so the fine midpoint sits on the true wave
 *    while the coarse edge chords straight past it — a hairline of sky through the sea. The classic fixes
 *    are skirts (visible) or degenerate stitch strips (index bookkeeping). This mesh instead ships the fix
 *    as DATA: every odd vertex on a fine outer boundary carries `aStitch`, the offset to its two coarse
 *    neighbours, and the vertex shader displaces it to the MIDPOINT OF THE COARSE EDGE (average of the two
 *    neighbours' full Gerstner samples) instead of to its own wave height. The fine polyline then lies
 *    exactly on the coarse segment — provably crack-free, for any wave table, at any time, with no skirt,
 *    no degenerate triangle and no extra draw call. Cost: two extra wave evaluations on 576 of the
 *    21.7k vertices — and a bench proved the branch itself is free (see OCEAN_CELL0).
 *
 * The mesh is 100% COSMETIC. It does not touch `WAVE_DEFS`, the height function, buoyancy or the sim —
 * it only decides where the sea is SAMPLED for display.
 */
import * as THREE from 'three';

/**
 * Finest quad, right under the ship (metres) — 3.9× finer than the 7.81 m grid it replaces.
 *
 * TUNED ON A BENCH, not guessed. Swapping ONLY the geometry inside one live session (same shader, same
 * frame, same GPU state) showed the frame cost tracks NEAR-FIELD TESSELLATION DENSITY, not triangle count:
 *   - a 1500 m plane at 13.4 m cells (25 k tris) → 4.40 ms;  at 7.81 m cells (74 k tris) → 4.48 ms
 *   - the clipmap at 1.5 m cells → 4.75 ms;  at 2 m → 4.70 ms;  at 3 m → 4.62 ms   (all ~33-51 k tris)
 *   - doubling the mesh EXTENT changed nothing; the stitch branch changed nothing
 * i.e. the price of a fine near field is rasterizer quad overhead (a GPU shades 2×2 pixel quads, so small
 * triangles waste edge fragments), and it is ~0.2 ms at 1600×900 on an AMD iGPU — invisible against a
 * 16.7 ms vsync budget (measured rAF was 16.67 ms before AND after), but real. 2 m is the knee: below it
 * you pay more for a silhouette nobody can tell apart. Re-run that bench before going finer.
 */
export const OCEAN_CELL0 = 2;
/** Cells per side, per level. Must be a multiple of 4 (the ring hole is the middle M/2 cells). */
export const OCEAN_M = 72;
/** Number of levels (each doubles cell size and reach). */
export const OCEAN_LEVELS = 5;
/** Half-extent of level 0 (metres): the fine field reaches this far from the mesh centre. */
export const OCEAN_HALF0 = (OCEAN_M / 2) * OCEAN_CELL0;                       // 72 m
/** Half-extent of the whole clipmap (metres). */
export const OCEAN_OUTER = OCEAN_HALF0 * 2 ** (OCEAN_LEVELS - 1);             // 1152 m
/**
 * Translation snap (metres) = the COARSEST cell. Snapping to this keeps every level's vertices on one
 * fixed world lattice — the whole anti-swimming argument (see header). NEVER snap to anything finer.
 */
export const OCEAN_SNAP = OCEAN_CELL0 * 2 ** (OCEAN_LEVELS - 1);              // 32 m
/**
 * Radius (metres) where the sea is forced to exactly the horizon/fog colour, so it meets the sky dome
 * with no step. Matches the OLD mesh's along-axis fade (0.86…1.0 × 750 m) — the difference is that it is
 * now measured RADIALLY from the (unsnapped) captain, so the horizon no longer breathes as you turn.
 */
export const OCEAN_FADE_R = 750;

/** What `buildOceanClipmap()` produced — the numbers the renderer and the tests both read. */
export interface OceanClipmap {
  geometry: THREE.BufferGeometry;
  triangles: number;
  vertices: number;
  /** How many vertices carry a stitch offset (the LOD-seam midpoints). */
  stitched: number;
}

/** Level L's cell size / half-extent, in metres. */
export const oceanCell = (level: number): number => OCEAN_CELL0 * 2 ** level;
export const oceanHalf = (level: number): number => OCEAN_HALF0 * 2 ** level;

/**
 * Build the clipmap as ONE indexed BufferGeometry lying in the XZ plane (y = 0; the shader supplies the
 * height). Pure geometry — no material, no scene, no side effects — so a unit test can assert the seam.
 */
export function buildOceanClipmap(): OceanClipmap {
  const M = OCEAN_M;
  const V = M + 1;                       // vertices per side, per level
  const lo = M / 4, hi = (M * 3) / 4;    // the ring's hole, in this level's own cell indices

  const pos: number[] = [];
  const stitch: number[] = [];
  const index: number[] = [];
  let stitched = 0;

  for (let L = 0; L < OCEAN_LEVELS; L++) {
    const c = oceanCell(L);
    const h = oceanHalf(L);
    const hollow = L > 0;                 // level 0 is a full grid; every other level is a ring
    const base = pos.length / 3;
    // vertex map for this level: -1 = not emitted (strictly inside the hole)
    const vmap = new Int32Array(V * V).fill(-1);
    let next = 0;
    for (let jz = 0; jz < V; jz++) {
      for (let ix = 0; ix < V; ix++) {
        // a vertex is skipped only if it is STRICTLY inside the hole — the hole's boundary ring of
        // vertices belongs to this level too (its innermost cells need them).
        if (hollow && ix > lo && ix < hi && jz > lo && jz < hi) continue;
        vmap[jz * V + ix] = base + next++;
        pos.push(-h + ix * c, 0, -h + jz * c);
        // LOD seam: this level's OUTER boundary faces a level whose cells are 2c. Odd vertices along
        // that boundary are the T-junctions; hand them the offset to their two coarse neighbours.
        // (Corners are always even — M is a multiple of 4 — so they never stitch, and the two edges
        // meeting there agree.) The outermost level has no coarser neighbour: nothing to stitch.
        let sx = 0, sz = 0;
        if (L < OCEAN_LEVELS - 1) {
          const onZ = jz === 0 || jz === M;   // top/bottom edge: neighbours run along X
          const onX = ix === 0 || ix === M;   // left/right edge: neighbours run along Z
          if (onZ && ix % 2 === 1) sx = c;
          else if (onX && jz % 2 === 1) sz = c;
        }
        if (sx !== 0 || sz !== 0) stitched++;
        stitch.push(sx, sz);
      }
    }
    for (let jz = 0; jz < M; jz++) {
      for (let ix = 0; ix < M; ix++) {
        if (hollow && ix >= lo && ix < hi && jz >= lo && jz < hi) continue; // cell inside the hole
        const a = vmap[jz * V + ix]!, b = vmap[jz * V + ix + 1]!;
        const d = vmap[(jz + 1) * V + ix]!, e = vmap[(jz + 1) * V + ix + 1]!;
        index.push(a, d, b, b, d, e);
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geometry.setAttribute('aStitch', new THREE.Float32BufferAttribute(stitch, 2));
  geometry.setIndex(index);
  // the shader relocates every vertex anyway and the mesh is never frustum-culled, but a sane bound
  // keeps three's internal maths (and any future raycast) from seeing a zero-size box.
  geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), OCEAN_OUTER * Math.SQRT2);

  return { geometry, triangles: index.length / 3, vertices: pos.length / 3, stitched };
}
